import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/auth/useAuth';
import { api, ApiError } from '@/lib/api';

const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1, 'Password is required'),
});
type LoginValues = z.infer<typeof loginSchema>;

const appHandle = import.meta.env.VITE_SHOPIFY_APP_HANDLE;

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const [shopDomain, setShopDomain] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginValues>({ resolver: zodResolver(loginSchema) });

  if (auth.status === 'authenticated') {
    return <Navigate to="/shipments" replace />;
  }

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true);
    try {
      await api.post('/auth/login', values, 'standalone');
      await auth.refresh();
      navigate('/shipments', { replace: true });
    } catch (error) {
      const message =
        error instanceof ApiError && error.status === 401
          ? t('login.invalidCredentials')
          : t('login.failed');
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  });

  const openInShopify = () => {
    const normalized = shopDomain
      .trim()
      .replace(/^https?:\/\//, '')
      .replace(/\/$/, '');
    if (!normalized) {
      toast.error(t('login.shopDomainMissing'));
      return;
    }
    const domain = normalized.endsWith('.myshopify.com')
      ? normalized
      : `${normalized}.myshopify.com`;
    window.location.href = `https://${domain}/admin/apps/${appHandle}`;
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-muted/40 p-6">
      <div className="w-full max-w-md space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('login.title')}</CardTitle>
            <CardDescription>{t('login.subtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('login.email')}</Label>
                <Input id="email" type="email" autoComplete="email" {...register('email')} />
                {errors.email ? (
                  <p className="text-sm text-destructive">{errors.email.message}</p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t('login.password')}</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  {...register('password')}
                />
                {errors.password ? (
                  <p className="text-sm text-destructive">{errors.password.message}</p>
                ) : null}
              </div>
              <Button type="submit" disabled={submitting} className="w-full">
                {submitting ? t('login.submitting') : t('login.submit')}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('login.shopifyHeader')}</CardTitle>
            <CardDescription>{t('login.shopifyDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="shop">{t('login.shopDomainLabel')}</Label>
              <Input
                id="shop"
                placeholder={t('login.shopDomainPlaceholder')}
                value={shopDomain}
                onChange={(e) => setShopDomain(e.target.value)}
              />
            </div>
            <Button type="button" variant="outline" onClick={openInShopify} className="w-full">
              {t('login.openInShopify')}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
